import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Material UI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// API
import { search, is_JobRecord, JobRecord } from '../../api/api';
import { addJob } from '../../storage/MySearchesStore';
import { SearchCriteria, SearchModality, Species } from '../../Types';
import { spec } from 'node:test/reporters';
import AcceptEula from './AcceptEula';
import { getEULAAccepted } from '../../storage/EULAStore';

// Types
interface Props {
  searchFormValidated : boolean
  // Search Fields
  species : Species,
  gene : string,
  accessionNumber : string,
  sequence : string
}

const SearchButton = ({searchFormValidated, species, gene, accessionNumber, sequence}: Props) => {

  // Use Hooks to create local state.
  const [searching, setSearching] = useState<boolean>(false);

  // TODO: Zustand to accept EULA
  const [viewingEULA, setViewingEULA] = useState<boolean>(false);

  // Use the useNavigate Hook to be able to navigate to different routes.
  const navigate = useNavigate(); 

  // Helper function to call our API's search function
  const callSearch = () => {
    // Indicate that we are searching, set our local state variable for searching to true
    setSearching(true);

    // Using our API-Wrapper, Invoke the API Search function.
    console.log("Search Criteria:", species, gene, accessionNumber, sequence)

    // Build a SearchCriteria object to perform our search.
    const searchCriteria : SearchCriteria = {
      "modality" : SearchModality.SEARCH_BY_SPECIES_GENE_SEQUENCE,
      "species" : species,
      "gene" : gene,
      "accessionNumber" : accessionNumber,
      "sequence" : sequence
    }
    
    // Call the API's search method to perform a search.
    return search(searchCriteria).then((data) => {
      // If this code block triggers, we got back data from the API.
      // Check to see if we got an instance of a JobRecord
      if(is_JobRecord(data)){
        // If we got a JobRecord, cast the data object to this type so we can resolve type-members.
        let jobRecord : JobRecord = data as JobRecord;
        // Add the searchCriteria to the job record
        jobRecord.searchCriteria = searchCriteria;

        // If we got here this request was successful
        console.log("Job created:", jobRecord);
        addJob(jobRecord);
        // Now that we have our response, navigate to the search results view for this job.
        navigate("/search/" + jobRecord.uuid);
      }
    }).catch((err) => {
      // If this code block triggers, an error happened.
      console.error(err);
      // If there was an error issuing our Request, denote that issue to the user.
      setSnackbarOpen(true);
    }).finally(() => {
      // This code block triggers regardless of if we had success or failure.
      setSearching(false);
    });
  }

  const viewEula = () => {
    setViewingEULA(true);
  }

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarOpen(false);
  };

  const handleModalClose = () => {

  }

  const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };

  return (
    <>
      {/* Search and accept EULA button */}
      <Grid container>
        <Grid item xs={12} sm={6}>
          <Button variant="contained" onClick={viewEula}  sx={{marginRight: { xs: "0px", sm: "4px"}, width: {xs:'100%', sm:"calc(100% - 4px)"}}} style={{height:'42px'}}>
            Terms and Conditions
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} style={{width:"100%", alignContent:'center'}}>
          <Box textAlign='center'>
            <Button disabled={!getEULAAccepted() || searching || !searchFormValidated} variant="contained" onClick={callSearch}  sx={{marginLeft: { xs: "0px", sm: "4px"}, width: {xs:'100%', sm:"calc(100% - 4px)"}}} style={{height:'42px'}}>Search</Button>
          </Box>
        </Grid>
        {/* Indicate that we are searching. */}
      </Grid>
      {/* EULA */}
      <AcceptEula open={viewingEULA} onCloseRequest={() => {
        // close the EULA Modal
        setViewingEULA(false);
      }}></AcceptEula>
      {/* Modal Dialogue to show searching */}
      <Modal
        open={searching}
        onClose={handleModalClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{width:'100%', height:'100%'}}
      >
        <Box sx={modalStyle}>
          <CircularProgress/>
        </Box>
      </Modal>
      {/* Snackbar to show search error */}
      <div>
        <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleClose}>
          <Alert
            onClose={handleClose}
            severity="error"
            variant="filled"
            sx={{ width: '100%' }}
          >
            Error communicating with API
          </Alert>
        </Snackbar>
      </div>
    </>
  );
};

export default SearchButton;